import {
  useCnaeLabel,
  useCnaeSearch,
} from '@/features/quotes/hooks/group-life/use-cnae-search.hook';
import { useDebouncedValue } from '@/shared/utils';
import {
  Combobox,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | { message?: string };
};

/**
 * Combobox searchable de CNAE — busca SERVER-SIDE (DataTable `CNAE` do
 * InsureMO) por **código ou descrição**. O catálogo CNAE é grande demais para
 * carregar inteiro no browser, então filtramos no servidor (o back roteia por
 * código quando o termo é numérico, senão por descrição).
 *
 * Usado em:
 *  - Step 1 Estipulante (Atividade Principal)
 *  - Modal Adicionar Estipulação (Atividade Principal do subestipulante)
 *
 * O valor persistido é o CÓDIGO; exibimos `código — descrição` quando
 * resolvido, senão o próprio código (edição de cotação já salva).
 */
export function CnaeCombobox({
  value,
  onChange,
  label = 'Atividade Principal',
  placeholder = 'Selecione a atividade',
  required = false,
  disabled = false,
  error,
}: Props) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const { data: items, isFetching } = useCnaeSearch(debouncedSearch);

  const options = useMemo(
    () =>
      (items ?? []).map(c => ({
        value: String(c.code),
        label: `${c.code} — ${c.description}`,
      })),
    [items],
  );

  // Cache de `código → "6209100 — descrição"` que persiste entre buscas. Sem
  // ele, ao selecionar um CNAE a busca muda e o item sai de `options`, fazendo
  // o trigger exibir só o código.
  const [labelByCode, setLabelByCode] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!options.length) return;
    setLabelByCode(prev => {
      let changed = false;
      const next = { ...prev };
      for (const o of options) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [options]);

  // Edição: resolve o label do código já salvo quando ele ainda não passou por
  // uma busca (hidratação de cotação existente).
  const cachedLabel = value ? labelByCode[value] : undefined;
  const { data: resolvedCnae } = useCnaeLabel(
    value && !cachedLabel ? value : undefined,
  );
  const resolvedLabel = resolvedCnae
    ? `${resolvedCnae.code} — ${resolvedCnae.description}`
    : '';
  useEffect(() => {
    if (value && resolvedLabel && labelByCode[value] !== resolvedLabel) {
      setLabelByCode(prev => ({ ...prev, [value]: resolvedLabel }));
    }
  }, [value, resolvedLabel, labelByCode]);

  const displayLabel = value
    ? (cachedLabel ?? (resolvedLabel || value))
    : null;

  const errorMessage =
    typeof error === 'string' ? error : error?.message ?? undefined;

  return (
    <Field.Root invalid={!!errorMessage} disabled={disabled}>
      <Field.Label className='text-alba-black-500'>
        {label}
        {required && <span className='text-alba-error-red-500'>*</span>}
      </Field.Label>
      <Combobox.Root
        items={options}
        value={value ? [value] : []}
        onValueChange={details => onChange(details.value[0] || '')}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Trigger>{displayLabel ?? placeholder}</Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder='Buscar por código ou descrição...'
              autoFocus
            />
            {isFetching ? (
              <div className='py-8 flex items-center justify-center'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : (
              <Combobox.List>
                {debouncedSearch.length === 1 ? (
                  <div className='py-4 text-center text-sm text-alba-gray-500'>
                    Digite ao menos 2 caracteres para buscar
                  </div>
                ) : options.length === 0 ? (
                  <div className='py-4 text-center text-sm text-alba-gray-500'>
                    Nenhuma atividade encontrada
                  </div>
                ) : (
                  <Combobox.ItemGroup>
                    <Combobox.ItemGroupLabel>
                      Atividades (CNAE)
                    </Combobox.ItemGroupLabel>
                    {options.map(item => (
                      <Combobox.Item key={item.value} item={item}>
                        <Combobox.ItemText>{item.label}</Combobox.ItemText>
                        <Combobox.ItemIndicator />
                      </Combobox.Item>
                    ))}
                  </Combobox.ItemGroup>
                )}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {errorMessage && <Field.ErrorText>{errorMessage}</Field.ErrorText>}
    </Field.Root>
  );
}
